// ./src/components/generic/MediaSelector/tabs/AudioLibraryTab.tsx
//
// Search the free MUSIC & SOUNDS library and pick a track (A2d #72 follow-up, owner 2026-10-01: "add audio / background
// sound services — similar to Unsplash for photo"). Searched through OUR API (`AudioLibraryService`) for the event whose
// page is being edited — its white label decides which libraries are offered and holds their keys.
//
// ⚠ A host must HEAR a track before choosing it, so every row has a preview button — and a preview of a large file takes
//   a moment, so the button shows that it is loading (the same complaint that started this work, on the block itself).
// ⚠ Every track offered allows commercial use of the whole work, and carries its credit; the pick hands the credit back
//   (`MediaSelectorResult.credit`) so the block can show it.
// ⚠ Branch order: sources not read yet → sources failed → nothing typed → search failed → no results → results.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActionIcon, Alert, Anchor, Box, Button, Center, Group, Loader, Paper, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { formatAudioTime } from "@em/blocks";
import { AUDIO_LIBRARY_MIN_QUERY } from "./audioLibraryRules";
import { AudioLibraryItem, AudioLibraryKind, AudioLibraryService, AudioLibrarySource } from "@/utils/api/AudioLibraryService";
import { refusalReason } from "@/utils";

import { AppIcon } from "../../AppIcon";
import { EmptyState } from "../../EmptyState";

export interface AudioLibraryTabProps {
  /** The event whose page is being edited (its white label decides the libraries and their keys). */
  eventId: string;
  /** Which kind the tab opens on — music for the main track, sounds for the background. */
  defaultKind: AudioLibraryKind;
  /** Shared search text — persists across tabs (lifted to the MediaSelector). */
  query: string;
  onQueryChange: (q: string) => void;
  /** URL of the currently-picked track (drives the selected highlight). */
  selectedUrl: string | null;
  /** Called with the chosen track, or null when deselected. */
  onPick: (item: AudioLibraryItem | null) => void;
}

type PreviewState = { id: string; status: "loading" | "playing" } | null;

export const AudioLibraryTab: React.FC<AudioLibraryTabProps> = ({ eventId, defaultKind, query, onQueryChange, selectedUrl, onPick }) => {
  const { t } = useTranslation(["common"]);
  const inputRef = useRef<HTMLInputElement>(null);

  const [sources, setSources] = useState<AudioLibrarySource[]>([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [sourcesError, setSourcesError] = useState<string | null>(null);
  const [kind, setKind] = useState<AudioLibraryKind>(defaultKind);
  const [source, setSource] = useState<AudioLibrarySource["source"] | null>(null);

  const [submitted, setSubmitted] = useState("");
  const [items, setItems] = useState<AudioLibraryItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const callRef = useRef(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [preview, setPreview] = useState<PreviewState>(null);

  // The libraries this event's white label can search.
  useEffect(() => {
    let alive = true;

    AudioLibraryService.sources(eventId).then((result) => {
      if (!alive) {
        return;
      }

      setSourcesLoaded(true);

      if (result.didSucceed()) {
        setSources((result.data as AudioLibrarySource[]) ?? []);
        setSourcesError(null);
      } else {
        setSourcesError(refusalReason(result, t("common:audioLibrary.sourcesFailed", { defaultValue: "Could not load the sound libraries." })));
      }
    });

    return () => {
      alive = false;
    };
  }, [eventId, t]);

  const sourcesForKind = sources.filter((s) => s.kinds.includes(kind));
  const activeSource = sourcesForKind.find((s) => s.source === source) ?? sourcesForKind[0] ?? null;

  const stopPreview = useCallback(() => {
    audioRef.current?.pause();
    setPreview(null);
  }, []);

  // Never leave a preview playing behind a closed drawer.
  useEffect(() => stopPreview, [stopPreview]);

  const runSearch = useCallback(
    async (q: string, nextPage: number) => {
      if (!activeSource) {
        return;
      }

      const my = ++callRef.current;

      setLoading(true);

      const result = await AudioLibraryService.search({ eventId, source: activeSource.source, kind, query: q, page: nextPage });

      if (my !== callRef.current) {
        return;
      }

      setLoading(false);

      if (!result.didSucceed()) {
        setSearchError(refusalReason(result, t("common:audioLibrary.searchFailed", { defaultValue: "Could not search the sound library." })));

        return;
      }

      const data = ((result.data as any[]) ?? [])[0] ?? { items: [], hasMore: false };

      setSearchError(null);
      setItems((prev) => (nextPage === 1 ? data.items ?? [] : [...prev, ...(data.items ?? [])]));
      setHasMore(!!data.hasMore);
      setPage(nextPage);
    },
    [eventId, activeSource, kind, t],
  );

  // Search-as-you-type, 700 ms after the last key and from 3 letters — every miss spends a provider quota shared with
  // other hosts, so "r", "ra", "rai" are never searched (review, 2026-10-01). Again when the kind or library changes.
  const [debouncedQuery] = useDebouncedValue(query, 700);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();

    if (trimmed.length < AUDIO_LIBRARY_MIN_QUERY || !activeSource) {
      return;
    }

    setSubmitted(trimmed);
    onPick(null);
    stopPreview();
    void runSearch(trimmed, 1);
  }, [debouncedQuery, activeSource, runSearch, onPick, stopPreview]);

  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);

    return () => window.clearTimeout(id);
  }, []);

  const togglePreview = (item: AudioLibraryItem) => {
    if (preview?.id === item.id) {
      stopPreview();

      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
    }

    const audio = audioRef.current;

    audio.pause();
    audio.src = item.url;
    setPreview({ id: item.id, status: "loading" });
    audio
      .play()
      .then(() => setPreview((p) => (p?.id === item.id ? { id: item.id, status: "playing" } : p)))
      .catch(() => setPreview((p) => (p?.id === item.id ? null : p)));
    audio.onended = () => setPreview((p) => (p?.id === item.id ? null : p));
  };

  const pick = (item: AudioLibraryItem) => {
    onPick(selectedUrl === item.url ? null : item);
  };

  if (!sourcesLoaded) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (sourcesError) {
    return (
      <Alert color="red" variant="light" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
        {sourcesError}
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <SegmentedControl
        size="md"
        value={kind}
        onChange={(v) => {
          setKind(v as AudioLibraryKind);
          setItems([]);
          setSubmitted("");
        }}
        data={[
          { value: "music", label: t("common:mediaSelector.audioMusic", { defaultValue: "Music" }) },
          { value: "sounds", label: t("common:mediaSelector.audioSounds", { defaultValue: "Sounds" }) },
        ]}
      />

      {sourcesForKind.length > 1 && (
        <SegmentedControl
          size="md"
          value={activeSource?.source ?? ""}
          onChange={(v) => {
            setSource(v as AudioLibrarySource["source"]);
            setItems([]);
            setSubmitted("");
          }}
          data={sourcesForKind.map((s) => ({ value: s.source, label: s.name }))}
        />
      )}

      <TextInput
        ref={inputRef}
        size="md"
        label={
          kind === "music"
            ? t("common:mediaSelector.audioSearchMusic", { defaultValue: "Search free music" })
            : t("common:mediaSelector.audioSearchSounds", { defaultValue: "Search free sounds" })
        }
        placeholder={
          kind === "music"
            ? t("common:mediaSelector.audioSearchMusicPh", { defaultValue: "Try 'acoustic', 'party', 'piano'…" })
            : t("common:mediaSelector.audioSearchSoundsPh", { defaultValue: "Try 'rain', 'ocean waves', 'crowd'…" })
        }
        value={query}
        onChange={(e) => onQueryChange(e.currentTarget.value)}
        leftSection={loading && page === 1 ? <Loader size={16} /> : <AppIcon iconName="Search" size={16} />}
        rightSection={
          query ? (
            <ActionIcon variant="subtle" color="gray" onClick={() => onQueryChange("")} aria-label="Clear">
              <AppIcon iconName="X" size={14} />
            </ActionIcon>
          ) : undefined
        }
      />

      {!submitted ? (
        <EmptyState
          iconName="Music"
          title={
            kind === "music"
              ? t("common:mediaSelector.audioPromptMusic", { defaultValue: "Find free music" })
              : t("common:mediaSelector.audioPromptSounds", { defaultValue: "Find a free sound" })
          }
          description={t("common:mediaSelector.audioPromptDesc", {
            defaultValue: "Type a few words above. Only tracks whose licence allows commercial use are shown — keep the credit that comes with each one.",
          })}
        />
      ) : searchError ? (
        <Alert color="orange" variant="light" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
          {searchError}
        </Alert>
      ) : items.length === 0 && !loading ? (
        <Text fz="md" c="dimmed" ta="center" py="md">
          {t("common:mediaSelector.audioNoResults", { defaultValue: "Nothing found — try different words." })}
        </Text>
      ) : (
        <Stack gap="xs">
          {items.map((item) => {
            const selected = selectedUrl === item.url;
            const previewing = preview?.id === item.id;
            const meta = [item.creator, formatAudioTime(item.durationSec ?? null), item.licenseLabel].filter(Boolean).join(" · ");

            return (
              <Paper
                key={`${item.source}-${item.id}`}
                withBorder
                radius="md"
                p="sm"
                role="button"
                tabIndex={0}
                onClick={() => pick(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    pick(item);
                  }
                }}
                style={{
                  cursor: "pointer",
                  borderColor: selected ? "var(--mantine-color-blue-5)" : undefined,
                  boxShadow: selected ? "0 0 0 2px var(--mantine-color-blue-2)" : undefined,
                }}
              >
                <Group gap="sm" wrap="nowrap" align="center">
                  <ActionIcon
                    size="lg"
                    radius="xl"
                    variant={previewing ? "filled" : "light"}
                    aria-label={previewing ? "Stop the preview" : "Listen"}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePreview(item);
                    }}
                  >
                    {previewing && preview?.status === "loading" ? (
                      <Loader size={16} color="currentColor" />
                    ) : (
                      <AppIcon iconName={previewing ? "PlayerPause" : "PlayerPlay"} size={18} />
                    )}
                  </ActionIcon>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fz="md" fw={600} truncate>
                      {item.title}
                    </Text>
                    <Text fz="md" c="dimmed" truncate>
                      {meta}
                    </Text>
                  </Box>
                  {selected ? <AppIcon iconName="Check" size={18} color="var(--mantine-color-blue-6)" /> : null}
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {loading && page > 1 && (
        <Center py="sm">
          <Loader size="sm" />
        </Center>
      )}

      {hasMore && !loading && !searchError && (
        <Center>
          <Button variant="subtle" size="md" onClick={() => runSearch(submitted, page + 1)}>
            {t("common:buttons.loadMore", { defaultValue: "Load more" })}
          </Button>
        </Center>
      )}

      {activeSource && items.length > 0 && (
        <Text fz="md" c="dimmed" ta="center">
          {t("common:mediaSelector.poweredBy", { defaultValue: "Powered by" })}{" "}
          <Anchor href={activeSource.url} target="_blank" rel="noopener noreferrer" fw={600}>
            {activeSource.name}
          </Anchor>
        </Text>
      )}
    </Stack>
  );
};
